import React, { memo } from 'react';
import classNames from 'classnames';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDTableRow from '../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EkonomiskAnalysTableCell from '../EkonomiskAnalysTableCell/EkonomiskAnalysTableCell';
import { IFormatter } from '../../../../../../../Utils/Formatters/FormatterTypes';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { IRow } from '../../Queries/EkonomiskAnalysQuery/EkonomiskAnalysQuery';
import EkonomiskAnalysDetail from '../EkonomiskAnalysDetail/EkonomiskAnalysDetail';
import { getCurrencyFormatterParams } from '../../Utils/paramUtils';
import {
  tableIndexToColumnIndex,
  columnIndexToTableIndex,
} from '../../Utils/tableUtils';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { ekanalys_ekanalys_columns } from '../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { fieldInfoToColumnData } from '../../Utils/fieldInfoUtils';
import { IColumn } from '../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import { EkonomiskAnalysContextConsumer } from '../Context/EkonomiskAnalysContext';
import { TEDUrvalType } from '../../../../../../../Types/UrvalTypes';

interface IProps {
  columns: IColumn[];
  row: IRow;
  level: number;
  analysSettings: IFilledLocalAnalys;
  codePrivilegesId: string;
  dates: IEkonomiskAnalysFilledDates;
  denomination: number;
  nrDecimals: number;
  endastBokfort: boolean;
  expandedLevel: number;
  grundUrvalId: string | undefined;
  detailedSelection?: TEDUrvalType;
  visaSummeringar: boolean;
  utanSummeringSumColCount?: number;
  forceHelarsJamforelse: boolean;
}

interface IInnerProps extends IProps {
  accessibilityMode: boolean;
  classes?: any;
}

interface IState {
  isOpen: boolean;
  currFormatter: IFormatter;
  percentFormatter: IFormatter;
  detailColumn?: number;
}

const styles = (
  theme: Theme,
  _params: any,
  classes: Record<'hasDetailsSelected', string>
) => ({
  selectedRowBG: {
    backgroundColor:
      theme.customProperties.EkonomiskAnalysRowColors.selectedRowBGColor,
  },
  tableCell: {
    position: 'relative',
    '& .colPaint': {
      display: 'none',
      position: 'absolute',
      left: 0,
      width: '100%',
      backgroundColor: theme.customProperties.Fade.BlueGray.selectedLightFade,
      mixBlendMode: 'multiply',
      zIndex: 0,
      pointerEvents: 'none',
    },
    '&:hover .colPaint': {
      display: 'block',
    },
  },
  hasDetails: {
    '&.accessibility h6 , &.accessibility p ': {
      borderBottom: '2px solid ' + theme.customProperties.Fade.Teal.lowEmphasis,
      marginBottom: -2,
      [`.${classes.hasDetailsSelected}&`]: {
        borderBottomColor: theme.customProperties.Fade.LightOnDark.lowEmphasis,
        '&:hover': {
          borderBottomColor:
            theme.customProperties.Fade.LightOnDark.mediumEmphasis,
        },
      },
    },
    '& h6, & p': {
      '&:hover': {
        borderBottom:
          '2px solid ' + theme.customProperties.Fade.Teal.mediumEmphasis,
        marginBottom: -2,
      },
      [`.${classes.hasDetailsSelected}&`]: {
        '&:hover': {
          borderBottomColor:
            theme.customProperties.Fade.LightOnDark.mediumEmphasis,
        },
      },
    },
  },
  hasDetailsSelected: {},
  lastCell: {
    '&:last-child': {
      padding: 0,
    },
  },
  lastSumCol: {
    borderRight:
      '1px solid ' + theme.customProperties.EkonomiskAnalysTableBorderColor,
  },
});

/**
 * En rad i tabellen av en enalys
 */
export class EkonomiskAnalysRowBase extends React.PureComponent<
  IInnerProps,
  IState
> {
  constructor(props: IInnerProps) {
    super(props);
    const currFormatter = findFormatter('TEDFormatCurr');
    const percentFormatter = findFormatter('TEDFormatIntAsPercent');

    this.state = {
      isOpen: props.expandedLevel > props.level,
      detailColumn: undefined,
      currFormatter,
      percentFormatter,
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.expandedLevel !== this.props.expandedLevel) {
      const currFormatter = findFormatter('TEDFormatCurr');
      const percentFormatter = findFormatter('TEDFormatIntAsPercent');
      this.setState({
        isOpen: this.props.expandedLevel > this.props.level,
        detailColumn: undefined,
        currFormatter,
        percentFormatter,
      });
    }
  }

  toggleDetails = (i: number) => () => {
    if (i > 0) {
      const columnIndex = tableIndexToColumnIndex(
        i,
        this.props.utanSummeringSumColCount ??
          this.props.analysSettings.sums.length,
        this.props.visaSummeringar
      );
      const detailColumn =
        this.state.detailColumn === columnIndex ? undefined : columnIndex;

      this.setState({
        detailColumn,
        isOpen: false,
      });
    }
  };

  toggleOpen = (i: number) => () => {
    if (i === 0) {
      this.setState((oldState: IState) => ({
        isOpen: !oldState.isOpen,
        detailColumn: undefined,
      }));
    }
  };

  getSelectedColumnIndex = (): number | undefined => {
    if (this.state.detailColumn === undefined) {
      return undefined;
    }
    if (!this.props.visaSummeringar) {
      return this.state.detailColumn;
    }

    return this.state.detailColumn - this.props.analysSettings.sums.length + 1;
  };

  getSelectedColumnData = (): ekanalys_ekanalys_columns | undefined =>
    this.state.detailColumn !== undefined
      ? fieldInfoToColumnData(
          this.props.columns[this.state.detailColumn].fieldInfo
        )
      : undefined;

  getIngaendeVarde = (): number => {
    const ingVardeColIndex = this.props.columns.findIndex(col =>
      col.fieldInfo.some(
        fi =>
          fi.name === 'FALTKOD' &&
          fi.value?.__typename === 'TEDMixedString' &&
          fi.value.asString === 'PERINGVARDE'
      )
    );
    if (ingVardeColIndex !== -1) {
      return Number(
        this.props.row.data[
          columnIndexToTableIndex(
            ingVardeColIndex,
            this.props.utanSummeringSumColCount ??
              this.props.analysSettings.sums.length,
            this.props.visaSummeringar
          )
        ].value
      );
    }
    return 0;
  };

  showTableInfo = (
    e: React.MouseEvent<HTMLTableCellElement | HTMLTableHeaderCellElement>
  ) => {
    let currentElement: HTMLElement = e.currentTarget;
    let currentElementOffset = currentElement.offsetTop;
    let currentAncestor: HTMLTableElement | null =
      currentElement.closest('table');
    if (currentAncestor && currentElementOffset) {
      let currentAncestorHeight = currentAncestor.clientHeight;
      let currentAncestorOffset = currentAncestor.offsetTop;
      let currentPainter: HTMLElement | null =
        currentElement.querySelector('.colPaint');
      let yPos: string =
        '-' + (currentElementOffset - currentAncestorOffset) + 'px';
      if (currentPainter) {
        currentPainter.style.top = yPos;
        currentPainter.style.height = currentAncestorHeight + 'px';
      }
    }
  };

  getFirstYearInVisibleColumns = (columns: IColumn[]): number => {
    const year = columns.reduce((prev, curr) => {
      if (!curr.visible || curr.fieldType !== 'ekreportcol') {
        return prev;
      }

      let year = 0;

      const columnFromYearValue = curr.fieldInfo.find(
        info => info.name === 'FROM_AR'
      )?.value;

      const fromYearRelativeValue = curr.fieldInfo.find(
        info => info.name === 'FROM_AR_RELATIV'
      )?.value;

      if (
        columnFromYearValue?.__typename === 'TEDMixedInt' &&
        fromYearRelativeValue?.__typename === 'TEDMixedBoolean'
      ) {
        // columnFromYearValue kan innehålla negativa värden där av måste vi alltid addera
        year = columnFromYearValue.asInt ?? 0;

        if (fromYearRelativeValue.asBoolean) {
          year = this.props.dates.from.year + year;
        }

        return year < prev ? year : prev;
      }

      return prev;
    }, 9999);

    return year;
  };

  render() {
    const hasChildren =
      this.props.row.children && this.props.row.children.length > 0;
    const padding = hasChildren
      ? 24 + 16 * this.props.level
      : 40 + 16 * this.props.level;

    return (
      <>
        <EDTableRow
          hover={this.state.detailColumn === undefined}
          className={classNames({
            [this.props.classes.selectedRowBG]:
              this.state.detailColumn !== undefined,
          })}
          style={{ paddingLeft: padding }}
          lightBackground={!hasChildren}
          intermediateBackground={hasChildren && this.props.level === 1}
          parentBackground={this.props.level === 0}
        >
          {this.props.row.data.map((d, i) => {
            const formatter =
              (this.props.visaSummeringar && i > 0) ||
              (!this.props.visaSummeringar &&
                i >=
                  (this.props.utanSummeringSumColCount ??
                    this.props.analysSettings.sums.length))
                ? d.displayType === 'percent'
                  ? this.state.percentFormatter
                  : this.state.currFormatter
                : undefined;

            const hasDetails =
              (d.hasDetails && d.value !== '' && d.value !== '0') ||
              (d.hasChildValues && d.hasDetails && d.value !== '');

            let tooltipText: string | undefined;
            if (this.props.visaSummeringar) {
              tooltipText =
                i === 0 && this.props.analysSettings.sums[this.props.level]
                  ? this.props.analysSettings.sums[this.props.level].Namn ||
                    undefined
                  : hasDetails
                  ? this.state.detailColumn != null &&
                    this.state.detailColumn ===
                      tableIndexToColumnIndex(
                        i,
                        this.props.utanSummeringSumColCount ??
                          this.props.analysSettings.sums.length,
                        this.props.visaSummeringar
                      )
                    ? 'Dölj detaljer'
                    : 'Visa detaljer'
                  : '';
            } else {
              const isSummeringsCol =
                i <
                (this.props.utanSummeringSumColCount ??
                  this.props.analysSettings.sums.length);
              tooltipText = isSummeringsCol
                ? d.tooltip
                : hasDetails
                ? this.state.detailColumn != null &&
                  this.state.detailColumn ===
                    tableIndexToColumnIndex(
                      i,
                      this.props.utanSummeringSumColCount ??
                        this.props.analysSettings.sums.length,
                      this.props.visaSummeringar
                    )
                  ? 'Dölj detaljer'
                  : 'Visa detaljer'
                : '';
            }

            const alignment =
              (this.props.visaSummeringar && i === 0) ||
              (!this.props.visaSummeringar &&
                i <
                  (this.props.utanSummeringSumColCount ??
                    this.props.analysSettings.sums.length))
                ? 'left'
                : this.getSelectedColumnIndex() === i
                ? 'space-between'
                : 'right';

            return (
              <EkonomiskAnalysTableCell
                key={'cell-' + i}
                disablePointer={!this.props.visaSummeringar}
                selected={this.getSelectedColumnIndex() === i}
                rowSelected={this.state.detailColumn !== undefined}
                onClick={
                  this.props.visaSummeringar && i === 0
                    ? this.toggleOpen(i)
                    : hasDetails
                    ? this.toggleDetails(i)
                    : undefined
                }
                textVariant={hasChildren ? 'subtitle2' : 'body2'}
                alignment={alignment}
                pointer={(this.props.visaSummeringar && i === 0) || hasDetails}
                paddingLeft={
                  i === 0 && this.props.visaSummeringar ? padding : 32
                }
                showExpandMore={i === 0 && !this.state.isOpen && hasChildren}
                showExpandLess={i === 0 && this.state.isOpen && hasChildren}
                showDetailsIcon={this.getSelectedColumnIndex() === i}
                tooltipText={tooltipText}
                value={d.value}
                formatter={formatter}
                formatterParams={
                  formatter
                    ? d.displayType === 'percent'
                      ? 'DisplayZeroAsBlank="Y";Decimals="1"'
                      : getCurrencyFormatterParams(
                          this.props.columns[
                            tableIndexToColumnIndex(
                              i,
                              this.props.utanSummeringSumColCount ??
                                this.props.analysSettings.sums.length,
                              this.props.visaSummeringar
                            )
                          ],
                          this.props.denomination,
                          this.props.nrDecimals,
                          d.hasChildValues && d.hasDetails ? 'N' : 'Y'
                        )
                    : undefined
                }
                onMouseOver={e => this.showTableInfo(e)}
                className={classNames({
                  [this.props.classes.tableCell]:
                    !this.props.visaSummeringar || i !== 0,
                  [this.props.classes.hasDetails]: d.hasDetails && i !== 0,
                  [this.props.classes.hasDetailsSelected]:
                    d.hasDetails &&
                    i !== 0 &&
                    this.state.detailColumn !== undefined,
                  ['accessibility']: this.props.accessibilityMode,
                  [this.props.classes.lastSumCol]:
                    !this.props.visaSummeringar &&
                    i ===
                      (this.props.utanSummeringSumColCount ??
                        this.props.analysSettings.sums.length) -
                        1,
                })}
              />
            );
          })}
          <EDTableCell className={this.props.classes.lastCell} />
        </EDTableRow>
        {this.state.isOpen && this.props.row.children && hasChildren && (
          <>
            {this.props.row.children.map((x, i) => (
              <EnhancedEkonomiskAnalysRow
                codePrivilegesId={this.props.codePrivilegesId}
                key={'children-row-' + i}
                columns={this.props.columns}
                row={x}
                level={this.props.level + 1}
                analysSettings={this.props.analysSettings}
                dates={this.props.dates}
                denomination={this.props.denomination}
                nrDecimals={this.props.nrDecimals}
                endastBokfort={this.props.endastBokfort}
                expandedLevel={this.props.expandedLevel}
                grundUrvalId={this.props.grundUrvalId}
                visaSummeringar={this.props.visaSummeringar}
                forceHelarsJamforelse={this.props.forceHelarsJamforelse}
              />
            ))}
          </>
        )}
        {this.state.detailColumn !== undefined && (
          <EkonomiskAnalysDetail
            budgetDetailSummationMode={
              this.props.analysSettings.other.budgetSummationMode
            }
            codePrivilegesId={this.props.codePrivilegesId}
            columnData={this.getSelectedColumnData()}
            sums={this.props.analysSettings.sums}
            sumValues={this.props.row.sumValues || []}
            rowLevel={this.props.level}
            show={this.state.detailColumn !== undefined}
            numberOfColumns={
              this.props.visaSummeringar
                ? this.props.columns.length -
                  this.props.analysSettings.sums.length +
                  1
                : this.props.columns.length
            } // Första kolumnerna ligger visuellt i den första kolumnen
            dates={this.props.dates}
            endastBokfort={this.props.endastBokfort}
            forceHelarsjamforelse={this.props.forceHelarsJamforelse}
            summationMode={this.props.analysSettings.other.summationMode}
            grundUrvalId={this.props.grundUrvalId}
            lopsaldoDetaljrad={this.props.analysSettings.lopsaldoDetaljrad}
            ingaendeVardeLopSaldo={this.getIngaendeVarde()}
            detailedSelection={this.props.detailedSelection}
            visaSummeringar={this.props.visaSummeringar}
            forstaBokfar={this.getFirstYearInVisibleColumns(this.props.columns)}
          />
        )}
      </>
    );
  }
}

const EkonomiskAnalysRow = withStyles(EkonomiskAnalysRowBase, styles);

const EnhancedEkonomiskAnalysRow = (props: IProps) => {
  return (
    <EkonomiskAnalysContextConsumer>
      {({ accessibilityMode }) => (
        <EkonomiskAnalysRow accessibilityMode={accessibilityMode} {...props} />
      )}
    </EkonomiskAnalysContextConsumer>
  );
};

export default memo(EnhancedEkonomiskAnalysRow);
